import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/auth"; // Verifique o caminho

// Hook para chamadas de API
const useApi = () => {
    const { currentUser } = useAuth();
    return useCallback(async (url, method = 'GET', data = null) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = { method, headers: { 'Authorization': `Bearer ${token}` } };
        if (data && method !== 'GET') {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(data);
        }
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha na requisição para ${url}`);
        }
        return response.json();
    }, [currentUser]);
};

// Componente principal do Modal
function ModalImportarPedidos({ isVisible, onClose, onConcluirImportacao }) {
    const [etapa, setEtapa] = useState(1); // 1: Pedidos, 2: Itens, 3: Consolidado
    const [loading, setLoading] = useState(false);

    // Data States
    const [pedidosDisponiveis, setPedidosDisponiveis] = useState([]);
    const [pedidosSelecionados, setPedidosSelecionados] = useState(new Set());
    const [itensDisponiveis, setItensDisponiveis] = useState([]);
    const [itensSelecionados, setItensSelecionados] = useState(new Set());
    const [todosItensSelecionados, setTodosItensSelecionados] = useState(false);

    // Search & Pagination States
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // Detalhes Modal State
    const [itensDetalhePedido, setItensDetalhePedido] = useState(null);

    const apiCall = useApi();

    // Reset ao abrir
    useEffect(() => {
        if (!isVisible) {
            setSearchTerm('');
            setCurrentPage(1);
            setItensDetalhePedido(null);
        }
    }, [isVisible]);

    // Busca Pedidos (Etapa 1)
    useEffect(() => {
        if (isVisible && etapa === 1) {
            const fetchPedidos = async () => {
                setLoading(true);
                try {
                    const data = await apiCall('/api/producao/pedidos-para-producao');
                    setPedidosDisponiveis(data || []);
                } catch (err) {
                    Swal.fire('Erro', err.message, 'error');
                } finally {
                    setLoading(false);
                }
            };
            fetchPedidos();
        }
    }, [isVisible, etapa, apiCall]);

    // Filter & Pagination Logic
    const filteredPedidos = useMemo(() => {
        if (!searchTerm) return pedidosDisponiveis;
        const lowerTerm = searchTerm.toLowerCase();
        return pedidosDisponiveis.filter(p =>
            String(p.numero).toLowerCase().includes(lowerTerm) ||
            String(p.cliente_nome || '').toLowerCase().includes(lowerTerm)
        );
    }, [pedidosDisponiveis, searchTerm]);

    const paginatedPedidos = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredPedidos.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredPedidos, currentPage]);

    const totalPages = Math.ceil(filteredPedidos.length / itemsPerPage);

    // Handlers
    const handleTogglePedido = (pedidoId) => {
        setPedidosSelecionados(prev => {
            const newSet = new Set(prev);
            if (newSet.has(pedidoId)) newSet.delete(pedidoId);
            else newSet.add(pedidoId);
            return newSet;
        });
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset page on search
    };

    const handleVerItens = async (pedidoId) => {
        try {
            // Reusing existing endpoint logic or creating a specific fetch
            // Here we use the generic items fetch filtered by this ID to match Etapa 2 logic, 
            // OR we could fetch /api/pedidos/{id} if we want raw data.
            // Let's use the one that returns items compatibale with our context if possible, 
            // but for "View" raw items is fine. Let's use existing endpoint for consistency.
            const data = await apiCall(`/api/producao/itens-de-pedidos?ids=${pedidoId}`);
            setItensDetalhePedido(data || []);
        } catch (err) {
            Swal.fire('Erro', "Não foi possível carregar os itens.", 'error');
        } finally {
        }
    };

    const irParaEtapa2 = async () => {
        if (pedidosSelecionados.size === 0) {
            Swal.fire('Atenção', 'Selecione pelo menos um pedido.', 'warning');
            return;
        }
        setLoading(true);
        try {
            const ids = Array.from(pedidosSelecionados).join(',');
            const data = await apiCall(`/api/producao/itens-de-pedidos?ids=${ids}`);
            setItensDisponiveis(data || []);
            const todosIds = new Set(data.map(item => item.pedido_item_id));
            setItensSelecionados(todosIds);
            setTodosItensSelecionados(true);
            setEtapa(2);
        } catch (err) {
            Swal.fire('Erro', err.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // ... (Existing Handlers for Etapa 2 & 3)
    const handleToggleItem = (itemId) => {
        setItensSelecionados(prev => {
            const newSet = new Set(prev);
            if (newSet.has(itemId)) newSet.delete(itemId);
            else newSet.add(itemId);
            return newSet;
        });
    };

    useEffect(() => {
        setTodosItensSelecionados(itensDisponiveis.length > 0 && itensSelecionados.size === itensDisponiveis.length);
    }, [itensSelecionados, itensDisponiveis.length]);

    const handleToggleTodosItens = () => {
        if (todosItensSelecionados) {
            setItensSelecionados(new Set());
            setTodosItensSelecionados(false);
        } else {
            const todosIds = new Set(itensDisponiveis.map(item => item.pedido_item_id));
            setItensSelecionados(todosIds);
            setTodosItensSelecionados(true);
        }
    };

    const irParaEtapa3 = () => {
        if (itensSelecionados.size === 0) {
            Swal.fire('Atenção', 'Selecione pelo menos um item.', 'warning');
            return;
        }
        setEtapa(3);
    };

    // Consolidação Logic
    const itensConsolidados = useMemo(() => {
        if (etapa !== 3) return [];
        const mapa = new Map();
        for (const item of itensDisponiveis) {
            if (!itensSelecionados.has(item.pedido_item_id)) continue;
            const gradeBaseSoma = (item.tamanhos_do_item || []).reduce((sum, t) => sum + (Number(t.quantidade) || 0), 0);
            const itemTotalUnidades = Number(item.quantidade_total) || 0;
            let itemVolumes = 0;
            if (gradeBaseSoma > 0) {
                itemVolumes = Math.round(itemTotalUnidades / gradeBaseSoma);
            } else {
                itemVolumes = itemTotalUnidades;
            }
            if (itemTotalUnidades > 0 && itemVolumes === 0) itemVolumes = 1;

            const key = `${item.produto_id}|${item.cor || 'N/A'}|${item.grade_id || 0}`;

            if (!mapa.has(key)) {
                mapa.set(key, {
                    idTemp: Date.now() + Math.random(),
                    produtoId: item.produto_id,
                    produto_codigo: item.produto_codigo,
                    produto_descricao: item.produto_descricao,
                    cor: item.cor || 'N/A',
                    gradeId: item.grade_id || null,
                    grade_descricao: item.grade_descricao || null,
                    tamanhos_json: item.tamanhos_do_item,
                    status: 'Planejado',
                    quantidade_concluida: 0,
                    quantidade_volumes: 0,
                    quantidade_total_unidades: 0,
                    source_pedido_item_ids: [],
                });
            }
            const consolidado = mapa.get(key);
            consolidado.quantidade_volumes += itemVolumes;
            consolidado.quantidade_total_unidades += itemTotalUnidades;
            consolidado.source_pedido_item_ids.push(item.pedido_item_id);
        }
        return Array.from(mapa.values());
    }, [etapa, itensDisponiveis, itensSelecionados]);

    const concluir = () => {
        onConcluirImportacao(itensConsolidados);
        handleClose();
    };

    const handleClose = () => {
        setEtapa(1);
        setPedidosDisponiveis([]);
        setPedidosSelecionados(new Set());
        setItensDisponiveis([]);
        setItensSelecionados(new Set());
        setTodosItensSelecionados(false);
        setItensDetalhePedido(null);
        onClose();
    };

    if (!isVisible) return null;

    // Styles
    const modalStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1055
    };
    const contentStyle = {
        backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '900px',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)', position: 'relative'
    };
    const bodyStyle = { maxHeight: '60vh', overflowY: 'auto', minHeight: '300px' };

    // Sub-modal styles
    const subModalOverlay = {
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.95)', zIndex: 1060,
        display: 'flex', flexDirection: 'column', padding: '20px', borderRadius: '8px'
    };

    return (
        <div style={modalStyle}>
            <div style={contentStyle}>

                {/* SUB-MODAL DETALHES */}
                {itensDetalhePedido && (
                    <div style={subModalOverlay}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0 text-primary">Detalhes do Pedido</h5>
                            <button className="btn-close" onClick={() => setItensDetalhePedido(null)}></button>
                        </div>
                        <div className="flex-grow-1 overflow-auto">
                            <table className="table table-sm table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Produto</th>
                                        <th>Cor</th>
                                        <th>Grade</th>
                                        <th className="text-end">Qtde</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itensDetalhePedido.map((it, idx) => (
                                        <tr key={idx}>
                                            <td>{it.produto_codigo} - {it.produto_descricao}</td>
                                            <td>{it.cor || '-'}</td>
                                            <td>{it.grade_descricao || '-'}</td>
                                            <td className="text-end">{it.quantidade_total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="text-end mt-2">
                            <button className="btn btn-secondary btn-sm" onClick={() => setItensDetalhePedido(null)}>Fechar Detalhes</button>
                        </div>
                    </div>
                )}

                {/* Cabeçalho Main */}
                <div className="modal-header">
                    <h5 className="modal-title">
                        {etapa === 1 && "Importar Pedidos (Etapa 1 de 3: Selecione os Pedidos)"}
                        {etapa === 2 && "Importar Pedidos (Etapa 2 de 3: Selecione os Itens)"}
                        {etapa === 3 && "Importar Pedidos (Etapa 3 de 3: Itens Consolidados)"}
                    </h5>
                    <button type="button" className="btn-close" onClick={handleClose}></button>
                </div>

                <div className="modal-body" style={bodyStyle}>
                    {loading && <div className="text-center p-5"><div className="spinner-border text-primary" role="status"></div></div>}

                    {/* ETAPA 1: PEDIDOS */}
                    {!loading && etapa === 1 && (
                        <>
                            {/* SEARCH BAR */}
                            <div className="mb-3">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0 ps-0"
                                        placeholder="Buscar por Nº do Pedido ou Cliente..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                    />
                                </div>
                            </div>

                            <table className="table table-sm table-hover align-middle">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>Sel.</th>
                                        <th>Nº Pedido</th>
                                        <th>Cliente</th>
                                        <th>Data</th>
                                        <th className='text-end'>Qtde. Total</th>
                                        <th className='text-center' style={{ width: '100px' }}>Produtos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedPedidos.map(p => (
                                        <tr key={p.id}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={pedidosSelecionados.has(p.id)}
                                                    onChange={() => handleTogglePedido(p.id)}
                                                />
                                            </td>
                                            <td className="fw-bold">{p.numero}</td>
                                            <td>{p.cliente_nome}</td>
                                            <td>{new Date(p.data_cadastro).toLocaleDateString('pt-BR')}</td>
                                            <td className='text-end'>{p.quantidade_total_pedido}</td>
                                            <td className='text-center'>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-info btn-sm rounded-pill px-3"
                                                    onClick={() => handleVerItens(p.id)}
                                                    title="Ver produtos deste pedido"
                                                >
                                                    <i className="fas fa-eye"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {paginatedPedidos.length === 0 && (
                                        <tr><td colSpan="6" className="text-center text-muted py-4">Nenhum pedido encontrado.</td></tr>
                                    )}
                                </tbody>
                            </table>

                            {/* PAGINATION */}
                            {totalPages > 1 && (
                                <nav className="d-flex justify-content-center mt-3">
                                    <ul className="pagination pagination-sm mb-0">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Anterior</button>
                                        </li>
                                        {[...Array(totalPages)].map((_, i) => (
                                            <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                <button type="button" className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button type="button" className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>Próxima</button>
                                        </li>
                                    </ul>
                                </nav>
                            )}
                        </>
                    )}

                    {/* ETAPA 2: ITENS */}
                    {!loading && etapa === 2 && (
                        <table className="table table-sm table-hover">
                            <thead>
                                <tr>
                                    <th><input type="checkbox" className="form-check-input" checked={todosItensSelecionados} onChange={handleToggleTodosItens} title="Selecionar Todos" /></th>
                                    <th>Pedido</th><th>Cliente</th><th>Produto</th><th>Cor</th><th className='text-end'>Qtde.</th><th>Grade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itensDisponiveis.map(item => (
                                    <tr key={item.pedido_item_id}>
                                        <td><input type="checkbox" className="form-check-input" checked={itensSelecionados.has(item.pedido_item_id)} onChange={() => handleToggleItem(item.pedido_item_id)} /></td>
                                        <td>{item.pedido_numero}</td>
                                        <td>{item.cliente_nome}</td>
                                        <td>{item.produto_codigo} - {item.produto_descricao}</td>
                                        <td>{item.cor || 'N/A'}</td>
                                        <td className='text-end'>{item.quantidade_total}</td>
                                        <td>{item.grade_descricao || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* ETAPA 3: CONSOLIDADOS */}
                    {!loading && etapa === 3 && (
                        <table className="table table-sm table-hover">
                            <thead><tr>
                                <th>Produto</th>
                                <th>Cor</th>
                                <th>Grade</th>
                                <th className='text-end'>Qtde. Volumes</th>
                                <th className='text-end'>Qtde. Total Unidades</th>
                            </tr></thead>
                            <tbody>
                                {itensConsolidados.map(item => (
                                    <tr key={item.idTemp}>
                                        <td>{item.produto_codigo} - {item.produto_descricao}</td>
                                        <td>{item.cor}</td>
                                        <td>{item.grade_descricao || '—'}</td>
                                        <td className='text-end fw-bold'>{item.quantidade_volumes}</td>
                                        <td className='text-end fw-bold'>{item.quantidade_total_unidades}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="modal-footer gap-2">
                    <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancelar</button>
                    {etapa === 1 && <button type="button" className="btn btn-primary" onClick={irParaEtapa2} disabled={loading || pedidosSelecionados.size === 0}>Avançar</button>}
                    {etapa === 2 && (
                        <>
                            <button type="button" className="btn btn-secondary" onClick={() => setEtapa(1)}>Voltar</button>
                            <button type="button" className="btn btn-primary" onClick={irParaEtapa3} disabled={loading || itensSelecionados.size === 0}>Avançar</button>
                        </>
                    )}
                    {etapa === 3 && (
                        <>
                            <button type="button" className="btn btn-secondary" onClick={() => setEtapa(2)}>Voltar</button>
                            <button type="button" className="btn btn-success" onClick={concluir} disabled={loading}>Concluir Importação</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ModalImportarPedidos;